import { createStyles, FormControl, FormControlLabel, FormLabel, IconButton, makeStyles, Radio, RadioGroup, Theme } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { ChartCommonProps, IconButtonWrapper } from '../Common';
import CloseIcon from '@material-ui/icons/Close';
import { convertStringNumberToNumber, formatNumber, GetArrow, getLabels, getMaxTickLimit, getSuggestedMaxTick } from '../../helpers/ChartHelper';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend, Scale, Tick, Chart, ChartEvent, ActiveElement } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { SelectedItemProps } from '../../home/MattersByStatusChart';
import { ChartFilter } from '../../home/ChartComponents';
import { useTranslation } from 'react-i18next';
import { TranslationTextList } from '../../locales/common';
import { ChartInfo } from '../../components/ChartInfo';

import { RV_CHART_CLASS, RV_CHART_COLORS, rvChartAnimation, rvValueAxisStyle, rvCategoryAxisStyle, rvTooltipStyle, isChartDataEmpty } from '../../home/chartTheme';
import { ChartHeading } from '../../home/ChartHeading';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import { AnimatedNumber } from '../../home/AnimatedNumber';
import { ChartEmptyState } from '../../home/ChartEmptyState';
import ShowChartOutlinedIcon from '@material-ui/icons/ShowChartOutlined';
ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend, zoomPlugin);

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            // padding: 15,
            display: 'flex',
            flexFlow: 'column',
            textAlign: 'center',
            fontSize: '18px',
            height: '100%',
            borderRadius: '8px',
            backgroundColor: '#FFFFFF',
            boxShadow: '0 0 14px 0 rgba(81,115,255,0.09)',
            width: '100%',
            position: 'relative',
            paddingBottom: '10px',
            '@media print': {
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
                boxShadow: 'none !important',
            },
        },
        popover: {},
        paper: {},
        radioGroup: {
            flexFlow: 'row wrap',
            justifyContent: 'center',
        },
        closeIcon: {
            color: 'rgb(0, 120, 212)',
        },
        titleContainer: {
            display: 'flex',
            flexFlow: 'column',
            padding: '20px 15px 0px 15px',
        },
        barContainer: {
            padding: '5px 15px 15px 15px',
            minHeight: '300px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            maxHeight: '350px',
            maxWidth: 'auto',
            position: 'relative',
            '@media (max-width: 533px)': {
                maxWidth: '350px',
                margin: '0 auto',
            },
            '& .titleWrapper': {
                textAlign: 'center',
                display: 'inline-block',
                width: '100%',
                padding: '15px',
                '& .title': {
                    fontSize: '18px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                '& .value': {
                    fontSize: '24px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                '& .secondary-title': {
                    fontSize: '16px',
                    fontWeight: 400,
                    display: 'block',
                    marginBottom: '15px',
                },
            },
        },
        loadingWrapper: {
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            backgroundColor: '#FFFFFF',
            boxShadow: '0 0 14px 0 rgba(81,115,255,0.09)',
        },
        title: {
            fontSize: 16,
            fontWeight: 600,
            fontFamily: 'inherit',
            marginBottom: 0,
        },
        totalDiv: {
            cursor: 'pointer',
            paddingRight: 5,
        },
        total: {
            fontSize: '2.5rem',
            padding: '10px 0',
            fontWeight: 600,
            cursor: 'pointer',
        },
        span: {
            fontSize: '1rem',
        },
        helpIcon: {
            position: 'absolute',
            left: '5px',
            top: '5px',
            '@media print': {
                display: 'none !important',
            },
        },
        formControl: {
            display: 'flex',
            flexFlow: 'row wrap',
            alignItems: 'center',
            justifyContent: 'center',
        },
        formLabel: {
            marginRight: '10px',
        },
        footer: {
            fontSize: '16px',
            textAlign: 'center',
        },
    }),
);

export enum LeadsByType {
    CREATED = 0,
    ENQUIRY,
}

interface NewLeads12MonthsProps extends SelectedItemProps, ChartCommonProps {
    chartFilter?: ChartFilter;
}

interface ChartState {
    symbol: string;
    options: LeadsByType;
    months: string[];
    newLeadsCount: number[];
    trendCount: number[];
    lastPeriodCount: number;
    previousPeriodCount: number;
    costPerLead: number;
}

export const NewLeadsInLast12MonthsTotal: React.FC<NewLeads12MonthsProps> = (props) => {
    const classes = useStyles();

    const { t } = useTranslation() as {
        t: (key: keyof TranslationTextList) => string;
    };

    // tslint:disable-next-line:no-any
    const chartRef = React.useRef<any>();

    const currencySymbolQuery = useQuery<CurrencySymbolData>(CURRENCY_SYMBOL_QUERY);
    
    const [state, setState] = useState<ChartState>({
        symbol: '$',
        options: LeadsByType.CREATED,
        months: [],
        newLeadsCount: [],
        trendCount: [],
        lastPeriodCount: 0,
        previousPeriodCount: 0,
        costPerLead: 0,
    });

    // tslint:disable-next-line:no-any
    const newLeadsQuery = useQuery<NewLeads12MonthsData>(NEW_LEADS_12_MONTHS, {
        variables: {
            entityType: props.chartFilter?.entityType,
            entityGuid: props.chartFilter?.entityGuid,
            responsibility: props.chartFilter?.responsibility,
        },
        fetchPolicy: 'cache-and-network',
    });

    useEffect(() => {
        setState((prevState) => {
            let months: string[] = [];
            let newLeadsCount: number[] = [];
            let trendCount: number[] = [];
            let lastPeriodCount: number = 0;
            let previousPeriodCount: number = 0;
            let tempCostPerLead: number = 0;
            if (
                newLeadsQuery.data &&
                newLeadsQuery.data.statistics &&
                newLeadsQuery.data.statistics.lead &&
                newLeadsQuery.data.statistics.lead.newLeadsInLast12Months &&
                !newLeadsQuery.loading &&
                !newLeadsQuery.error
            ) {
                const {
                    newLeads,
                    trend,
                    leadsPeriodInitialDateTotalCount,
                    leadsPeriodCreationDateTotalCount,
                    costPerLead
                } = newLeadsQuery.data.statistics.lead.newLeadsInLast12Months.newLeadsInLast12MonthsByPeriod;
                
                months = newLeads.map((l) => { return l.month; });

                if (state.options === LeadsByType.CREATED) {
                    newLeadsCount = newLeads.map((l) => { return l.createdDateCount; });
                    trendCount = trend.map((l) => { return l.createdDateCount; });
                    lastPeriodCount = leadsPeriodCreationDateTotalCount.lastPeriodTotalCount;
                    previousPeriodCount = leadsPeriodCreationDateTotalCount.previousPeriodTotalCount;
                    tempCostPerLead = costPerLead.costPerLeadCreatedDate;
                } else {
                    newLeadsCount = newLeads.map((l) => { return l.initialDateCount; });
                    trendCount = trend.map((l) => { return l.initialDateCount; });
                    lastPeriodCount = leadsPeriodInitialDateTotalCount.lastPeriodTotalCount;
                    previousPeriodCount = leadsPeriodInitialDateTotalCount.previousPeriodTotalCount;
                    tempCostPerLead = costPerLead.costPerLeadInitialEnquiry;
                }
            }

            return {
                ...prevState,
                months,
                newLeadsCount,
                trendCount,
                lastPeriodCount,
                previousPeriodCount,
                costPerLead: tempCostPerLead,
            };
        });
        // tslint:disable-next-line: align
    }, [newLeadsQuery.data, newLeadsQuery.loading, newLeadsQuery.error, state.options]);

    useEffect(() => {
        if (currencySymbolQuery.data && currencySymbolQuery.data.options && !currencySymbolQuery.loading && !currencySymbolQuery.error) {
            setState((prevState) => {
                return {
                    ...prevState,
                    symbol: currencySymbolQuery.data!.options.currencySymbol.symbol,
                };
            });
        }

        // tslint:disable-next-line: align
    }, [currencySymbolQuery.loading, currencySymbolQuery.error, currencySymbolQuery.data]); 

    const onCloseChart = () => {
        if (props.onCloseChart) {
            props.onCloseChart(`isTaskQueueByAssigneeChartOpen`);
        }
    };

    const onChangeRadio = (event: React.ChangeEvent<HTMLInputElement>) => {
        const fieldName = event.target.name;
        setState((prevState) => {
            return {
                ...prevState,
                // tslint:disable-next-line: radix
                [fieldName]: parseInt((event.target as HTMLInputElement).value),
            };
        });
    };

    const onPan = (context: { chart: Chart }) => {
        chartRef.current.options.scales.y.min = 0;
        chartRef.current.update();
    };

    const onHover = (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
        (event?.native?.target as HTMLElement).style.cursor = elements.length > 0 ? 'pointer' : 'auto';
    };
    
    if (newLeadsQuery.loading || currencySymbolQuery.loading) {
        return (
            <ChartSkeleton variant="line" />
        );
    }

    return (
        <div className={`${classes.root} ${RV_CHART_CLASS}${isChartDataEmpty(state.newLeadsCount, state.trendCount) ? ' rv-chart--empty' : ''}`}>
            <IconButtonWrapper aria-label="close">
                <ChartInfo
                    description={props.description}
                    fontSize={'medium'}
                    className={`${classes.helpIcon} rv-chart-help`}
                />
                <IconButton aria-label="close" onClick={onCloseChart} size="small">
                    <CloseIcon className={classes.closeIcon} />
                </IconButton>
            </IconButtonWrapper>
            <div className={classes.titleContainer}>
                <ChartHeading icon={<ShowChartOutlinedIcon />} title={t('New Enquiries in the last 12 months')} />
                <div className={`${classes.total} h2`}>
                    <AnimatedNumber value={state.lastPeriodCount} format={(amount) => formatNumber(amount)} />
                    {GetArrow(state.lastPeriodCount, state.previousPeriodCount)}
                </div>
                <div className={classes.title}>
                    {formatNumber(state.previousPeriodCount)} {t('new enquiries in the 12 months before that')}
                </div>
            </div>
            <div className={classes.barContainer}>
                <Line
                    ref={chartRef}
                    data={{
                        labels: getLabels(state.months),
                        datasets: [
                            {
                                fill: false,
                                backgroundColor: RV_CHART_COLORS.primarySoft,
                                borderColor: RV_CHART_COLORS.primarySoft,
                                borderWidth: 3,
                                hoverBackgroundColor: RV_CHART_COLORS.primarySoft,
                                hoverBorderColor: RV_CHART_COLORS.primarySoft,
                                data: state.trendCount,
                                pointStyle: 'dash',
                                borderDash: [10, 5],
                                tension: 0.35,
                                pointRadius: 0
                            },
                            {
                                label: t('Enquiries Opened'),
                                backgroundColor: RV_CHART_COLORS.primary,
                                borderColor: RV_CHART_COLORS.primary,
                                borderWidth: 3,
                                hoverBackgroundColor: RV_CHART_COLORS.primary,
                                hoverBorderColor: RV_CHART_COLORS.primary,
                                data: state.newLeadsCount,
                                tension: 0.35,
                                pointRadius: 3,
                                pointHoverRadius: 5
                            }
                        ]
                    }}
                    options={{
                        onHover: onHover,
                        indexAxis: 'x' as const,
                        animation: rvChartAnimation(),
                        elements: { bar: { borderWidth: 0 } },
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                            y: {
                                grid: rvValueAxisStyle.grid,
                                beginAtZero: true,
                                suggestedMax: getSuggestedMaxTick([...state.newLeadsCount, ...state.trendCount]),
                                min: 0,
                                ticks: {
                                    ...rvValueAxisStyle.ticks,
                                    maxTicksLimit: getMaxTickLimit([...state.newLeadsCount, ...state.trendCount]),
                                    autoSkip: false,
                                    callback: function (this: Scale, tickValue: number, index: number, ticks: Tick[]) {
                                        return `${formatNumber(tickValue)}`;
                                    }
                                }
                            },
                            x: {
                                grid: rvCategoryAxisStyle.grid,
                                ticks: {
                                    ...rvCategoryAxisStyle.ticks,
                                    autoSkip: false
                                }
                            }
                        },
                        plugins: {
                            legend: {
                                position: 'bottom' as const,
                                display: false
                            },
                            title: {
                                display: false,
                                text: `${t('Enquiries Opened in the last 12 months')}: ${formatNumber(state.lastPeriodCount)}`,
                            },
                            zoom: {
                                pan: {
                                    enabled: false,
                                    onPan: onPan
                                },
                                zoom: {
                                    wheel: {
                                        enabled: false
                                    }
                                }
                            },
                            tooltip: {
                                ...rvTooltipStyle,
                                callbacks: {
                                    // tslint:disable-next-line: no-any
                                    label: function (context: any) {
                                        const {
                                            // dataIndex, 
                                            // dataset,
                                            // label,
                                            formattedValue
                                        } = context;

                                        const currentValue = convertStringNumberToNumber(formattedValue);

                                        return `${t('Enquiries')} ${formatNumber(currentValue)}`;
                                    },
                                }
                            }
                        },
                    }}
                />
                {isChartDataEmpty(state.newLeadsCount, state.trendCount) && (
                    <ChartEmptyState title="No new enquiries" hint="Nothing in the last 12 months." />
                )}
            </div>
            <FormControl component="div" className={classes.formControl}>
                <FormLabel component="legend" className={classes.formLabel}>
                    Show
                </FormLabel>
                <RadioGroup name="options" value={state.options} onChange={onChangeRadio} className={classes.radioGroup}>
                    <FormControlLabel value={LeadsByType.CREATED} control={<Radio color="primary" />} label="Date Added" />
                    <FormControlLabel value={LeadsByType.ENQUIRY} control={<Radio color="primary" />} label={t('Enquiry Date')} />
                </RadioGroup>
            </FormControl>
            {/* {(props.chartFilter?.entityType === 4) &&
                <div className={classes.footer}>Cost per Enquiry: {`${state.symbol}${formatNumber(state.costPerLead)}`}</div>
            } */}
        </div>
    );
};

export interface CurrencySymbolData {
    options: Options;
}

export interface Options {
    currencySymbol: CurrencySymbol;
}

export interface CurrencySymbol {
    symbol: string;
}

export const CURRENCY_SYMBOL_QUERY = gql`
    query CurrencySymbol {
        options {
            currencySymbol {
                symbol
            }
        }
    }
`;

export interface NewLeads12MonthsData {
    statistics: Statistics;
}

export interface Statistics {
    lead: Leads;
}

export interface Leads {
    newLeadsInLast12Months: NewLeadsByPeriod;
}

export interface NewLeadsByPeriod {
    newLeadsInLast12MonthsByPeriod: NewLeads;
}

export interface NewLeads {
    newLeads: NewLead[];
    trend: NewLead[];
    leadsPeriodInitialDateTotalCount: LeadsSummary;
    leadsPeriodCreationDateTotalCount: LeadsSummary;
    costPerLead: CostPerLead;
}

export interface CostPerLead {
    costPerLeadCreatedDate: number;
    costPerLeadInitialEnquiry: number;
}

export interface LeadsSummary {
    lastPeriodTotalCount: number;
    previousPeriodTotalCount: number;
}

export interface NewLead {
    month: string;
    createdDateCount:   number;
    initialDateCount: number;
}

const NEW_LEADS_12_MONTHS = gql`
    query newLeadsIn12Months($entityType: Int, $entityGuid: String) {
        statistics {
            lead {
                newLeadsInLast12Months(entityType: $entityType, entityGuid: $entityGuid) {
                    newLeadsInLast12MonthsByPeriod {
                        newLeads {
                            month
                            createdDateCount
                            initialDateCount
                        }
                        trend {
                            month
                            createdDateCount
                            initialDateCount
                        }
                        leadsPeriodInitialDateTotalCount {
                            lastPeriodTotalCount
                            previousPeriodTotalCount
                        }
                        leadsPeriodCreationDateTotalCount {
                            lastPeriodTotalCount
                            previousPeriodTotalCount
                        }
                        costPerLead {
                          costPerLeadCreatedDate
                          costPerLeadInitialEnquiry
                        }
                    }
                }
            }
        }
    }
`;
